import ImageGallery from 'react-image-gallery';
import Skeleton from 'react-loading-skeleton';
import { Badge } from 'reactstrap';
import {
  CheckboxButton,
  IconButton,
  NormalButton,
  ConfirmModal,
  SuggestInput,
  TextInput,
  CloseIcon as closeIcon,
  ResetChangeRedIcon as resetIcon,
  TrashBoxRedIcon as deleteIcon,
  WhiteArrowBackIcon as arrowBack,
  WhiteArrowNextIcon as arrowNext,
  ColoredEditIcon as editIcon,
} from '@/shared/ui';
import { SUGGEST_COMMENT, TYPE_EVENT_OTHER } from '@/shared/config';
import { timezoneToOffset } from '@/shared/lib';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { CAPTURE_STRIP_DURATION } from '@/entities/strip';
import useEditNormalInfo from './useEditNormalInfo';

export type EditNormalInfoProps = {
  isEditBasicInfo?: boolean;
  couldRemoveStrip?: boolean;
  data?: any;
  originalData?: any;
  transferData?: any;
  handleClickCloseButton: () => void;
  handleEnterEditMoreInfo: (data: any) => void;
  handleRemoveStrip?: (data: any) => void;
  updateEditData: (data: any, isDone: boolean, type?: any) => void;
};

/**
 * Compact editor for a captured strip, showing its images in a gallery rather than a live ECG viewer. Renders reset changes and remove strip confirmation modals, the strip duration badge, validated title and technician comment inputs, loading and error placeholders for the images, and footer actions that behave differently in basic edit mode.
 *
 * @param props - The edit strip props, carrying data, originalData, the close handler and the remove strip handler.
 * @returns The rendered strip editor with its image gallery, comment fields and footer actions.
 */
const EditNormalInfo = ({
  isEditBasicInfo: isEditBasicInfoProp,
  couldRemoveStrip: couldRemoveStripProp,
  data,
  originalData,
  transferData,
  handleClickCloseButton,
  handleEnterEditMoreInfo,
  handleRemoveStrip,
  updateEditData,
}: EditNormalInfoProps) => {
  const {
    isEditBasicInfo,
    couldRemoveStrip,
    imageRef,
    state,
    setState,
    listImages,
    onClickDone,
    onChange,
    onClickCheckbox,
    onClickEditMoreInfo,
    onClickResetChanges,
  } = useEditNormalInfo({
    isEditBasicInfo: isEditBasicInfoProp,
    couldRemoveStrip: couldRemoveStripProp,
    data,
    originalData,
    transferData,
    handleClickCloseButton,
    handleEnterEditMoreInfo,
    updateEditData,
  });
  return (
    <>
      <ConfirmModal
        isOpen={state.isOpenResetChangeModal}
        onClickLeftButton={() => {
          setState({ isOpenResetChangeModal: false });
        }}
        onClickRightButton={onClickResetChanges}
        leftButtonName="Cancel"
        rightButtonName="Reset Changes"
        title="Reset all changes"
        isRedBtn
        isFilledRightBtn={false}
        customContent={
          <div>
            <div>Are you sure you want to reset all changes?</div>
            <div>This action cannot be undone, and any modifications will be lost</div>
          </div>
        }
      />
      <ConfirmModal
        isOpen={state.isOpenRemoveStripModal}
        onClickLeftButton={() => {
          setState({ isOpenRemoveStripModal: false });
        }}
        onClickRightButton={() => {
          setState({ isRemoved: true, isOpenRemoveStripModal: false });
        }}
        leftButtonName="Cancel"
        rightButtonName="Remove"
        title="Remove strip"
        isRedBtn
        isFilledRightBtn={false}
        question="Are you sure you want to remove this strip"
      />
      <div className="ai-strip-header">
        <div>
          {data?.start
            ? convertToMomentJs(
                data.start,
                data.timezoneOffset || timezoneToOffset(data.timezone),
              )?.format('MM/DD/YYYY, HH:mm:ss')
            : ''}{' '}
          <Badge className="duration-badge">{`${CAPTURE_STRIP_DURATION * (state.stripImgs?.length || 1)}s`}</Badge>
        </div>
        <div>
          {!isEditBasicInfo && (
            <IconButton
              disabled={state.isRemoved}
              className="mr3 edit-more-info-btn"
              isFilledIconWithText
              iconComponent={<img src={editIcon} alt="Edit icon" />}
              iconText="Edit more info"
              onClick={onClickEditMoreInfo}
            />
          )}
          <IconButton
            isFilled
            iconComponent={<img src={closeIcon} alt="Close icon" />}
            onClick={handleClickCloseButton}
          />
        </div>
      </div>
      <div className="ai-strip-content">
        <div className="ecg-strip">
          {state.stripImgs?.length ? (
            <>
              <ImageGallery
                ref={imageRef}
                items={listImages}
                infinite={false}
                showPlayButton={false}
                showFullscreenButton={false}
                showBullets
                lazyLoad
                disableKeyDown
                slideDuration={250}
                startIndex={0}
                thumbnailPosition="bottom"
                renderLeftNav={(onClick, disabled) => (
                  <button
                    className="image-gallery-custom-nav left"
                    disabled={disabled}
                    onClick={onClick}
                  >
                    <img src={arrowBack} alt="back" />
                  </button>
                )}
                renderRightNav={(onClick, disabled) => (
                  <button
                    className="image-gallery-custom-nav right"
                    disabled={disabled}
                    onClick={onClick}
                  >
                    <img src={arrowNext} alt="next" />
                  </button>
                )}
                onImageLoad={() => {
                  setState({
                    isLoading: false,
                    isLoadedImageError: false,
                  });
                }}
                onImageError={() => {
                  setState({
                    isLoading: false,
                    isLoadedImageError: true,
                  });
                }}
              />
              {state.isLoading ? (
                <div className="loading-thumbnail">
                  <Skeleton height="100%" />
                </div>
              ) : (
                state.isLoadedImageError && (
                  <div className="loading-thumbnail --error">
                    There is an error while loading ECG strips
                  </div>
                )
              )}
            </>
          ) : (
            <div className="no-ecg-strip-container">
              <span className="no-ecg-strip-text">There is no strip to display</span>
            </div>
          )}
        </div>
        {(originalData?.type === TYPE_EVENT_OTHER ||
          (isEditBasicInfo && data?.type === TYPE_EVENT_OTHER)) && (
          <div className="technician-comment mt3">
            <TextInput
              className="w-100 mt1 custom-techinician-comment-input"
              name="title"
              type="text"
              label="Title"
              placeholder="Enter title"
              value={state.title}
              onChange={onChange}
              disabled={state.isRemoved}
              isError={!!state.titleError}
              errorMessage={state.titleError}
              maxLength={100}
              id="titleInput"
            />
          </div>
        )}
        <div className="technician-comment mt3">
          <SuggestInput
            className="w-100 mt1 custom-techinician-comment-input"
            name="technicianComments"
            type="textarea"
            label="Technician comment"
            placeholder="Enter comment..."
            value={state.technicianComments}
            onChange={onChange}
            disabled={state.isRemoved}
            isError={!!state.commentError}
            errorMessage={state.commentError}
            maxLength={520}
            id="technicianInput"
            suggestTexts={SUGGEST_COMMENT}
            isReverse
            isFlexibleHeight
          />
        </div>
      </div>
      <div className="ai-strip-footer">
        <div className="left-part">
          {!isEditBasicInfo && (
            <div className="checkbox-group">
              <CheckboxButton
                disabled={state.isRemoved}
                className="mr3"
                buttonName="Include in report"
                checked={state.isIncludedToReport}
                onClick={() => {
                  onClickCheckbox('isIncludedToReport');
                }}
              />
              {state.isIncludedToReport && (
                <CheckboxButton
                  disabled={state.isRemoved}
                  className="mr3"
                  buttonName="Include strip"
                  checked={state.selected}
                  onClick={() => {
                    onClickCheckbox('selected');
                  }}
                />
              )}
            </div>
          )}
          {!state.isRemoved && (
            <IconButton
              className="delete-button"
              isIconWithText
              iconComponent={<img src={deleteIcon} alt="Delete icon" />}
              isFilled
              iconText="Remove strip"
              disabled={!couldRemoveStrip}
              onClick={() => {
                if (isEditBasicInfo) {
                  handleRemoveStrip(data);
                } else {
                  setState({ isOpenRemoveStripModal: true });
                }
              }}
              tooltip={couldRemoveStrip ? '' : 'Must have at least one Min/Max HR'}
            />
          )}
          {state.hasChanged && (
            <IconButton
              className="delete-button ml3"
              isIconWithText
              iconComponent={<img src={resetIcon} alt="Reset change icon" />}
              isFilled
              iconText="Reset changes"
              onClick={() => {
                setState({ isOpenResetChangeModal: true });
              }}
            />
          )}
        </div>
        <div className="right-part">
          <NormalButton className="mr3" buttonName="Cancel" onClick={handleClickCloseButton} />
          <NormalButton isFilled buttonName="Done" onClick={onClickDone} />
        </div>
      </div>
    </>
  );
};

export default EditNormalInfo;
